import { isName, record } from "@/utils/colour-values";
import {
  fillChangesInDark,
  ownColourCss,
  ownColourInDark,
} from "@/utils/colour-versions";
import { isLookSystem } from "@/utils/look-gate";
import { resolveHeaderSettings, type HeaderTop } from "@/utils/header-state";

const VARIABLES: Record<string, string> = {
  bgColor: "--header-bg-color",
  textColor: "--header-text-color",
  visibleTextColor: "--header-visible-text-color",
  menuBgColor: "--menu-bg-color",
  menuTextColor: "--menu-text-color",
};

/** The fill each text colour lies on. Only Standard draws its text on the header's background from the top. */
function fillUnder(key: string, headerType: unknown): string | undefined {
  if (key === "menuTextColor") return "menuBgColor";
  if (key === "visibleTextColor") return "bgColor";
  if (key === "textColor" && headerType === "DefaultHeader") return "bgColor";
  return undefined;
}

/** A fill that keeps its stored look in dark: an own colour whose dark version `derived.darkOwn` does not name. */
function keepsInDark(theme: unknown, fill: unknown): boolean {
  if (typeof fill !== "string") return false;
  const value = fill.trim().toLowerCase();
  return (
    !["", "transparent", "none", "inherit"].includes(value) &&
    !value.includes("var(--") &&
    !ownColourInDark(theme, fill)
  );
}

/**
 * A shared header's colours as its CSS variables. On the look system an own background or menu colour takes its dark
 * version where `derived.darkOwn` names it, and an own text colour where the fill under it changes in dark too; text
 * that lies over the page at the top (Gennemsigtig, Svævende) stays as stored.
 */
export function headerColourVariables(
  theme: unknown,
  colours: unknown,
  headerType: unknown,
): Record<string, unknown> {
  const stored = record(colours);
  const routes = (key: string) => {
    if (key === "bgColor" || key === "menuBgColor") return true;
    const fill = fillUnder(key, headerType);
    return !!fill && !keepsInDark(theme, stored[fill]);
  };
  return Object.fromEntries(
    Object.entries(stored)
      .filter(([key]) => VARIABLES[key])
      .map(([key, value]) => [
        VARIABLES[key],
        isLookSystem(theme) && typeof value === "string" && routes(key)
          ? ownColourCss(theme, value)
          : value,
      ]),
  );
}

/**
 * Gennemsigtig's and Svævende's text over the page at the top: Header › Tekst chosen as a palette colour as the
 * palette stores it in each scheme, not the version the dark rule settles on the header's fill. Gennemsigtig draws
 * --header-text-color only there; Svævende's bar draws it after the switch too, so it reads --re-header-top-text at the
 * top. A text colour of the header's own and a legacy override keep the header variables.
 */
export function headerTextOverPage(
  theme: unknown,
  colours: unknown,
  headerType: unknown,
): Record<string, string> {
  if (
    !isLookSystem(theme) ||
    (headerType !== "TransparentHeader" && headerType !== "FloatingHeader") ||
    typeof record(colours).textColor === "string"
  )
    return {};
  const colorSystem = record(theme.colorSystem);
  const { ref } = record(record(colorSystem.globals)["header-text"]);
  const inPalette =
    isName(ref) &&
    Array.isArray(colorSystem.palette) &&
    colorSystem.palette.some((entry) => record(entry).key === ref);
  if (!inPalette || "--header-text-color" in record(colorSystem.legacy))
    return {};
  const name =
    headerType === "TransparentHeader"
      ? "--header-text-color"
      : "--re-header-top-text";
  return { [name]: `var(--re-p-${ref})` };
}

/** Whether the fills under the menu's and the header actions' own text change in dark. */
export interface HeaderSurfaces {
  /** The header's bar, where it lies under its text from the top. */
  bar: boolean;
  /** The menu panel and its dropdowns (--menu-bg-color). */
  panel: boolean;
}

/**
 * The bar lies under the text where the header paints it from the top: a top that is Fast (HeaderStateController's
 * `top`, else resolveHeaderSettings on the drawn configuration `config` and the page's `page`). A header colour of its
 * own (`headers[].colours`) decides by its version; without one the global header and menu-panel roles paint, which
 * change in dark. Svævende's bar is its own `floating.surface`, whose fill is the header colour unless it sets one.
 */
export function headerSurfacesInDark(
  theme: unknown,
  header: { type: unknown; config?: unknown; page?: unknown; top?: HeaderTop },
  colours: unknown,
): HeaderSurfaces {
  const stored = record(colours);
  const changes = (fill: unknown) =>
    fill === undefined || fill === null || fill === ""
      ? true
      : fillChangesInDark(theme, fill);
  const top =
    header.top ??
    resolveHeaderSettings(
      record(record(theme).styleSystem).header,
      header.page,
      { ...record(header.config), type: header.type },
    ).top;
  const floating =
    header.type === "FloatingHeader"
      ? record(
          record(
            "floating" in record(header.page)
              ? record(header.page).floating
              : record(header.config).floating,
          ).surface,
        ).backgroundColor
      : undefined;
  return {
    bar: top === "solid" && changes(floating ?? stored.bgColor),
    panel: changes(stored.menuBgColor),
  };
}

/** Gennemsigtig's shadow behind the header (`overlay.fromBgColor`): an own colour takes its dark version. */
export function headerOverlayColour<T>(theme: unknown, colour: T): T {
  return isLookSystem(theme) && typeof colour === "string"
    ? (ownColourCss(theme, colour) as T)
    : colour;
}

/** Svævende's bar: its own fill takes its dark version, and its border with it where that fill changes. */
export function headerSurfaceInDark<
  T extends { backgroundColor?: string; borderColor?: string } | undefined,
>(theme: unknown, surface: T): T {
  if (!isLookSystem(theme) || !surface) return surface;
  const fill = surface.backgroundColor;
  const routed = { ...surface };
  if (typeof fill === "string")
    routed.backgroundColor = ownColourCss(theme, fill);
  if (typeof surface.borderColor === "string" && fillChangesInDark(theme, fill))
    routed.borderColor = ownColourCss(theme, surface.borderColor);
  return routed;
}
